import { tokenize } from "./tokenize";

/** Small domain expansions — a classic RAG trick when sparse retrieval misses synonyms. */
const QUERY_EXPANSIONS = {
  security: ["oauth", "jwt", "xss", "csrf", "identity", "b2c", "entra"],
  secure: ["oauth", "jwt", "xss", "csrf", "identity"],
  rag: ["langchain", "retrieval", "vector", "documentation", "qa"],
  agents: ["flowise", "tools", "memory", "mcp"],
  agent: ["flowise", "tools", "memory", "mcp"],
  cloud: ["azure", "aws", "kubernetes", "docker", "devops"],
  ai: ["langchain", "rag", "copilot", "llm", "genai"],
};

export const expandQueryTokens = (tokens) => {
  const expanded = [...tokens];
  tokens.forEach((token) => {
    (QUERY_EXPANSIONS[token] || []).forEach((extra) => {
      if (!expanded.includes(extra)) expanded.push(extra);
    });
  });
  return expanded;
};

/**
 * Lightweight BM25 retriever — the sparse first stage used in many hybrid RAG stacks.
 * Runs entirely in-browser with no API calls or model downloads.
 */
export class BM25Index {
  constructor(documents, { k1 = 1.5, b = 0.75 } = {}) {
    this.k1 = k1;
    this.b = b;
    this.documents = documents.map((doc) => {
      const tokens = doc.tokens ?? tokenize(doc.text);
      const termFreq = new Map();
      tokens.forEach((token) => termFreq.set(token, (termFreq.get(token) || 0) + 1));
      return { ...doc, tokens, termFreq, length: tokens.length };
    });

    this.avgdl =
      this.documents.reduce((sum, doc) => sum + doc.length, 0) /
      Math.max(this.documents.length, 1);

    this.documentFrequency = new Map();
    this.documents.forEach((doc) => {
      new Set(doc.tokens).forEach((token) => {
        this.documentFrequency.set(token, (this.documentFrequency.get(token) || 0) + 1);
      });
    });
  }

  idf(term) {
    const df = this.documentFrequency.get(term) || 0;
    const n = this.documents.length;
    return Math.log(1 + (n - df + 0.5) / (df + 0.5));
  }

  scoreDocument(queryTokens, doc) {
    let score = 0;
    queryTokens.forEach((term) => {
      const tf = doc.termFreq.get(term) || 0;
      if (!tf) return;
      const idf = this.idf(term);
      const numerator = tf * (this.k1 + 1);
      const denominator = tf + this.k1 * (1 - this.b + (this.b * doc.length) / this.avgdl);
      score += idf * (numerator / denominator);
    });
    return score;
  }

  search(query, { topK = 3 } = {}) {
    const baseTokens = tokenize(query);
    const queryTokens = expandQueryTokens(baseTokens);
    if (!queryTokens.length) return [];

    return this.documents
      .map((doc) => {
        const score = this.scoreDocument(queryTokens, doc);
        return {
          id: doc.id,
          source: doc.source,
          title: doc.title,
          text: doc.text,
          score,
          matchedTerms: queryTokens.filter((term) => doc.termFreq.has(term)),
          expandedTerms: queryTokens.filter((term) => !baseTokens.includes(term)),
        };
      })
      .filter((hit) => hit.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, topK);
  }
}
